import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Package, Loader2, Calendar, CreditCard, Info, Search } from "lucide-react";
import { useCreateShipment, useDealCreditConfig } from "@/hooks/use-order-shipments";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrders } from "@/hooks/use-orders";
import { DeliveryAddressBlock } from "./DeliveryAddressBlock";

interface CreateShipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  availableDevices: string[];
  totalCredits: number;
  allocatedCredits: number;
}

export const CreateShipmentDialog = ({
  open,
  onOpenChange,
  orderId,
  availableDevices,
}: CreateShipmentDialogProps) => {
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const createShipment = useCreateShipment();
  const { data: orders = [] } = useOrders();
  const currentOrder = orders.find((o) => o.id === orderId);
  const { data: dealConfig, isLoading: isLoadingDealConfig } = useDealCreditConfig(orderId);

  // Fetch unallocated devices from pool when no pre-allocated devices available
  const { data: unallocatedDevices = [], isLoading: isLoadingDevices } = useQuery({
    queryKey: ['unallocated-devices-for-shipment', orderId],
    queryFn: async () => {
      // Only show devices that have no organization_id (truly unallocated stock)
      const { data: devices, error: devicesError } = await supabase
        .from('device_details')
        .select('device_id, device_name')
        .is('organization_id', null)
        .order('device_name')
        .limit(5000);

      if (devicesError) {
        console.error('Error fetching devices:', devicesError);
        throw devicesError;
      }

      return devices || [];
    },
    enabled: open && availableDevices.length === 0
  });

  // Merge available devices - prefer pre-allocated, fall back to unallocated pool
  const devicesToShow = useMemo(() => {
    if (availableDevices.length > 0) {
      return availableDevices;
    }
    return unallocatedDevices.map(d => d.device_id);
  }, [availableDevices, unallocatedDevices]);

  // Create a map for device names (for display)
  const deviceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    unallocatedDevices.forEach(d => {
      map[d.device_id] = d.device_name || d.device_id;
    });
    return map;
  }, [unallocatedDevices]);

  // Calculate credits based on selected devices and deal configuration
  const creditBreakdown = useMemo(() => {
    if (!dealConfig || selectedDevices.length === 0) {
      return null;
    }

    const deviceCount = selectedDevices.length;
    const { coverageYears, annualCreditsPerCamera } = dealConfig;
    const currentYear = new Date().getFullYear();

    const years = Array.from({ length: coverageYears }, (_, i) => ({
      year: i + 1,
      calendarYear: currentYear + i,
      credits: deviceCount * annualCreditsPerCamera,
      isReleased: i === 0
    }));

    const totalCreditsForShipment = deviceCount * annualCreditsPerCamera * coverageYears;
    const year1Credits = deviceCount * annualCreditsPerCamera;

    return {
      years,
      totalCreditsForShipment,
      year1Credits,
      annualCreditsPerCamera,
      coverageYears
    };
  }, [dealConfig, selectedDevices]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDevices([]);
      setNotes('');
      setSearchQuery('');
    }
  }, [open]);

  const handleDeviceToggle = (deviceId: string) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const filteredDevices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return devicesToShow;
    return devicesToShow.filter((id) => {
      const name = deviceNameMap[id] || id;
      return id.toLowerCase().includes(q) || name.toLowerCase().includes(q);
    });
  }, [devicesToShow, deviceNameMap, searchQuery]);

  const allFilteredSelected =
    filteredDevices.length > 0 &&
    filteredDevices.every((id) => selectedDevices.includes(id));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedDevices((prev) => prev.filter((id) => !filteredDevices.includes(id)));
    } else {
      setSelectedDevices((prev) => Array.from(new Set([...prev, ...filteredDevices])));
    }
  };

  const handleSubmit = async () => {
    if (selectedDevices.length === 0) {
      return;
    }

    // Calculate total credits for this shipment (Year 1 credits × device count)
    const allocatedCredits = creditBreakdown?.year1Credits || 0;

    await createShipment.mutateAsync({
      orderId,
      allocatedDeviceIds: selectedDevices,
      allocatedCredits,
      notes: notes.trim() || undefined
    });

    onOpenChange(false);
  };

  const isValid = selectedDevices.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Create New Shipment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Deal Configuration Info */}
          {isLoadingDealConfig && (
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading deal configuration...</span>
            </div>
          )}

          {dealConfig && (
            <div className="p-3 bg-muted/30 rounded-md space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{dealConfig.dealTitle}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {dealConfig.coverageYears} year{dealConfig.coverageYears !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  {dealConfig.annualCreditsPerCamera.toLocaleString()} credits/device/year
                </span>
              </div>
            </div>
          )}

          {(currentOrder?.delivery_address_record || currentOrder?.delivery_address) && (
            <DeliveryAddressBlock
              address={currentOrder?.delivery_address_record}
              legacyAddress={currentOrder?.delivery_address}
              title="Ship To"
              compact
            />
          )}

          {/* Device Selection */}
          {isLoadingDevices && availableDevices.length === 0 && (
            <div className="flex items-center justify-center py-8 border rounded-md bg-muted/20">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Loading available devices...</span>
            </div>
          )}

          {!isLoadingDevices && devicesToShow.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>
                  Select Devices for this Shipment
                  {availableDevices.length === 0 && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      (from unallocated pool)
                    </span>
                  )}
                </Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAll}
                  className="text-xs"
                >
                  {allFilteredSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by device name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                {filteredDevices.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    No devices match "{searchQuery}"
                  </p>
                )}
                {filteredDevices.map((deviceId) => (
                  <div 
                    key={deviceId}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleDeviceToggle(deviceId)}
                  >
                    <Checkbox
                      checked={selectedDevices.includes(deviceId)}
                      onCheckedChange={() => handleDeviceToggle(deviceId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm font-mono">
                      {deviceNameMap[deviceId] || deviceId}
                    </span>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground">
                {selectedDevices.length} of {devicesToShow.length} devices selected
                {searchQuery && ` (${filteredDevices.length} shown)`}
              </p>
            </div>
          )}

          {!isLoadingDevices && devicesToShow.length === 0 && (
            <div className="text-center py-4 border rounded-md bg-muted/20">
              <p className="text-sm text-muted-foreground">
                No devices available for allocation. All devices have been assigned to shipments.
              </p>
            </div>
          )}

          {/* Credit Breakdown - Auto-calculated */}
          {creditBreakdown && (
            <div className="space-y-3">
              <Label>Credit Allocation (Auto-calculated)</Label>
              <div className="border rounded-md p-3 space-y-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">
                    {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''} × {creditBreakdown.annualCreditsPerCamera.toLocaleString()} credits/year
                  </span>
                </div>

                <div className="space-y-2">
                  {creditBreakdown.years.map((yearData) => (
                    <div 
                      key={yearData.year} 
                      className={`flex items-center justify-between p-2 rounded text-sm ${
                        yearData.isReleased 
                          ? 'bg-green-500/10 border border-green-500/20' 
                          : 'bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Year {yearData.year}</span>
                        <span className="text-muted-foreground">({yearData.calendarYear})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{yearData.credits.toLocaleString()}</span>
                        {yearData.isReleased ? (
                          <Badge variant="default" className="text-xs bg-green-500/20 text-green-700 border-green-500/30">
                            Released
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Reserved
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-2 border-t flex items-center justify-between text-sm">
                  <span className="font-medium">Total for {creditBreakdown.coverageYears} years</span>
                  <span className="font-mono font-medium">
                    {creditBreakdown.totalCreditsForShipment.toLocaleString()} credits
                  </span>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Only Year 1 credits will be released immediately. Year 2+ credits are reserved for future release.
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Shipment Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this shipment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Summary */}
          {isValid && creditBreakdown && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
              <Label className="text-xs text-muted-foreground">Shipment Summary</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="secondary">
                  {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''}
                </Badge>
                <Badge variant="secondary">
                  {creditBreakdown.year1Credits.toLocaleString()} Year 1 credits
                </Badge>
                {creditBreakdown.coverageYears > 1 && (
                  <Badge variant="outline">
                    +{(creditBreakdown.totalCreditsForShipment - creditBreakdown.year1Credits).toLocaleString()} reserved
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || createShipment.isPending}
            style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
            className="hover:opacity-90"
          >
            {createShipment.isPending ? 'Creating...' : 'Create Shipment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
